import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Alert
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

/**
 * PreCallDobModal — Collects DOB, time of birth, and place of birth before a call.
 *
 * - Pre-fills from user profile if data exists
 * - Mandatory before call proceeds
 * - Saves to user profile on submit
 */
const PreCallDobModal = ({ open, onClose, onSubmit }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    date_of_birth: '',
    time_of_birth: '',
    place_of_birth: '',
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pre-fill from user profile
  useEffect(() => {
    if (user && open) {
      setFormData({
        date_of_birth: user.date_of_birth ? user.date_of_birth.split('T')[0] : '',
        time_of_birth: user.time_of_birth
          ? new Date(user.time_of_birth).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
          : '',
        place_of_birth: user.place_of_birth || '',
      });
      setError(null);
    }
  }, [user, open]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!formData.date_of_birth || !formData.time_of_birth || !formData.place_of_birth) {
      setError('All fields are required before starting a call.');
      return;
    }

    setSaving(true);
    try {
      // Save to user profile
      await api.put('/profile', {
        date_of_birth: formData.date_of_birth,
        time_of_birth: `${formData.date_of_birth}T${formData.time_of_birth}:00Z`,
        place_of_birth: formData.place_of_birth,
      });

      onSubmit(formData);
    } catch (err) {
      console.error('Failed to save birth data:', err);
      // Still proceed — data collection is the priority, profile save is best-effort
      onSubmit(formData);
    } finally {
      setSaving(false);
    }
  };

  const hasAllData = formData.date_of_birth && formData.time_of_birth && formData.place_of_birth;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Birth Details Required</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please provide your birth details before the call. This helps the astrologer
          prepare your charts and provide accurate readings.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Date of Birth"
            type="date"
            value={formData.date_of_birth}
            onChange={handleChange('date_of_birth')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
          <TextField
            label="Time of Birth"
            type="time"
            value={formData.time_of_birth}
            onChange={handleChange('time_of_birth')}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
          <TextField
            label="Place of Birth"
            value={formData.place_of_birth}
            onChange={handleChange('place_of_birth')}
            placeholder="e.g., Chennai, Tamil Nadu, India"
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!hasAllData || saving}
        >
          {saving ? 'Saving...' : 'Proceed to Call'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreCallDobModal;
